import React, { useEffect, useState } from "react";
import { FormLabel, FormSelect } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import FormGroup from "react-bootstrap/FormGroup";
import Row from "react-bootstrap/Row";
import BootstrapTable from "react-bootstrap/Table";
import { injectIntl } from "react-intl";
import {
  AREA_FILTER_COL_KEY,
  filterRows,
  MEASUREMENT_FILTER_COL_KEY,
  renderFilterOptions,
  STATUS_FILTER_COL_KEY
} from "../../../../utils/FilterUtil";
import ObjectUtils, { coalesce } from "../../../../utils/ObjectUtils";
import { sortRows } from "../../../../utils/SortUtil";
import QuestionHeader from "./QuestionHeader";
import SortableTableQuestionHead from "./SortableTableQuestionHead";
import SortableTableRow from "./SortableTableRow";

const ActivityMonitoringQuestion = ({ question, answers, intl }) => {
  const [questionAnswer] = useState(convertActivityMonitoringMapAnswer(answers));
  const [rows, setRows] = useState(questionAnswer ? coalesce(questionAnswer.rows, []) : []);
  const [measureFilter, setMeasureFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [areaFilter, setAreaFilter] = useState(null);
  const [filteredRows, setFilteredRows] = useState(rows);
  pushProjectColumns(question);

  useEffect(() => {
    if (rows !== filteredRows) {
      setFilteredRows(rows);
      filterRows(rows, measureFilter, statusFilter, areaFilter, questionAnswer, setFilteredRows);
    }
  }, [rows, areaFilter, filteredRows, measureFilter, questionAnswer, statusFilter]);

  const handleFilterChange = (event, filterLabel) => {
    if (filterLabel === MEASUREMENT_FILTER_COL_KEY) {
      event.target.value !== "" ? setMeasureFilter(event.target.value) : setMeasureFilter(null);
    } else if (filterLabel === STATUS_FILTER_COL_KEY) {
      event.target.value !== "" ? setStatusFilter(event.target.value) : setStatusFilter(null);
    } else if (filterLabel === AREA_FILTER_COL_KEY) {
      event.target.value !== "" ? setAreaFilter(event.target.value) : setAreaFilter(null);
    }
  };

  useEffect(() => {
    filterRows(rows, measureFilter, statusFilter, areaFilter, questionAnswer, setFilteredRows);
  }, [measureFilter, statusFilter, areaFilter, questionAnswer, rows]);

  const sort = (identifier, asc) => {
    setRows(sortRows(rows, questionAnswer.answers, identifier, asc));
  };

  const filterFields = () => {
    return (
      <Row>
        <Col lg={4}>
          <FormGroup  className="mb-3">
            <FormLabel>{intl.formatMessage({ id: "user.surveyView.question.activity.filter.measurement" })}</FormLabel>
            <FormSelect          
              placeholder={intl.formatMessage({ id: "user.surveyView.dropdown.choose" })}
              onChange={(event) => handleFilterChange(event, MEASUREMENT_FILTER_COL_KEY)}
              value={measureFilter ? measureFilter : ""}
            >
              {renderFilterOptions(
                question.columns.find((col) => col.name === MEASUREMENT_FILTER_COL_KEY),
                questionAnswer,
                intl
              )}
            </FormSelect>
          </FormGroup>
        </Col>
        <Col lg={4}>
          <FormGroup  className="mb-3">
            <FormLabel>{intl.formatMessage({ id: "user.surveyView.question.activity.filter.status" })}</FormLabel>
            <FormSelect          
              placeholder={intl.formatMessage({ id: "user.surveyView.dropdown.choose" })}
              onChange={(event) => handleFilterChange(event, STATUS_FILTER_COL_KEY)}
              value={statusFilter ? statusFilter : ""}
            >
              {renderFilterOptions(
                question.columns.find((col) => col.name === STATUS_FILTER_COL_KEY),
                questionAnswer,
                intl
              )}
            </FormSelect>
          </FormGroup>
        </Col>
        <Col lg={4}>
          <FormGroup  className="mb-3">
            <FormLabel>{intl.formatMessage({ id: "user.surveyView.question.activity.filter.area" })}</FormLabel>
            <FormSelect
              placeholder={intl.formatMessage({ id: "user.surveyView.dropdown.choose" })}
              onChange={(event) => handleFilterChange(event, AREA_FILTER_COL_KEY)}
              value={areaFilter ? areaFilter : ""}
            >
              {renderFilterOptions(
                question.columns.find((col) => col.name === AREA_FILTER_COL_KEY),
                questionAnswer,
                intl
              )}
            </FormSelect>
          </FormGroup>
        </Col>
      </Row>
    );
  };

  return (
    <Row>
      <Col sm={12}>
        {question && (
          <QuestionHeader question={question} answered>
            {filterFields()}
            <BootstrapTable responsive>
              <SortableTableQuestionHead question={question} onSortRows={sort} />

              <tbody>
                {filteredRows.map((row, index) => {
                  const answers = coalesce(questionAnswer.answers, []).filter(
                    (rowAnswer) => rowAnswer.label === row.name
                  );

                  return (
                    <SortableTableRow
                      disabled
                      index={index}
                      question={question}
                      rows={filteredRows}
                      rowAnswers={answers}
                      skipFirst
                      key={index}
                      intl={intl}
                    />
                  );
                })}
              </tbody>
            </BootstrapTable>
          </QuestionHeader>
        )}
      </Col>
    </Row>
  );
};

export const pushProjectColumns = (question) => {
  if (!question?.columns?.find((col) => col.name === "projekt-0")) {
    question.columns.unshift(
      {
        infoText: "Bereich. ",
        name: "projekt-1",
        required: true,
        text: "Bereich",
        type: "Text",
        unchangeable: true,
        value: "projekt-1",
        visible: true
      },
      {
        infoText: "Der Name des Projektes. ",
        name: "projekt-0",
        required: true,
        text: "Projekt Name",
        type: "Text",
        unchangeable: true,
        value: "projekt-0",
        visible: true
      }
    );
  }
};

/**
 *
 * @param answers
 * @returns {[]}
 */
export const convertActivityMonitoringMapAnswer = (answers) => {
  let answerQuestion = [];
  answers?.forEach((answer, index) => {
    if (answerQuestion.length === 0) {
      answerQuestion = ObjectUtils.deepClone(answer.questionAnswer);
      changeRowLabelling(answerQuestion.answers, answerQuestion.rows, index);
      addProjectAnswer(answerQuestion.answers, answerQuestion.rows, answer.project);
    } else {
      let firstRows = ObjectUtils.deepClone(answerQuestion.rows);
      let secondRows = ObjectUtils.deepClone(answer.questionAnswer.rows);
      let firstAnswers = ObjectUtils.deepClone(answerQuestion.answers);
      let secondAnswers = ObjectUtils.deepClone(answer.questionAnswer.answers);
      changeRowLabelling(secondAnswers, secondRows, index);
      addProjectAnswer(secondAnswers, secondRows, answer.project);

      answerQuestion.rows = firstRows.concat(secondRows);
      answerQuestion.answers = firstAnswers.concat(secondAnswers);
    }
  });
  changeOrderIds(answerQuestion.rows);
  return answerQuestion;
};

/**
 *
 * @param existingRows
 * @param rows
 */
export const changeOrderIds = (rows) => {
  rows?.forEach((row, index) => {
    row.orderId = index;
  });
};

/**
 *
 * @param answer
 * @param rows
 * @param index
 */
export const changeRowLabelling = (answer, rows, index) => {
  rows?.forEach((row, i) => {
    const id = `answer-${index}-row-${i}`;
    answer?.forEach((ans) => {
      if (ans.label === row.id) {
        ans.label = id;
      }
    });
    row.id = id;
    row.name = id;
    row.value = id;
    row.orderId = index;
  });
};

/**
 *
 * @param answers
 * @param project
 */
export const addProjectAnswer = (answers, rows, project) => {
  rows?.forEach((row) => {
    const projectNameCol = {
      colLabel: "projekt-0",
      colText: "Projekt Name",
      columnType: "Text",
      label: row.id,
      name: "Option-1",
      text: project?.foreignId,
      value: project?.foreignId
    };
    if (!answers.find((answer) => answer.label === row.id && answer.colLabel === "projekt-0")) {
      answers.push(projectNameCol);
    }
    const projectIdCol = {
      colLabel: "projekt-1",
      colText: "Bereich",
      columnType: "Text",
      label: row.id,
      name: "Option-1",
      text: project?.programInstanceForeignId,
      value: project?.programInstanceForeignId
    };
    if (!answers.find((answer) => answer.label === row.id && answer.colLabel === "projekt-1")) {
      answers.push(projectIdCol);
    }
  });
};

export default injectIntl(ActivityMonitoringQuestion);
